import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { postMagazineRecommendedItemContent$key } from "scmp-app/queries/__generated__/postMagazineRecommendedItemContent.graphql";

import {
  Container,
  ContentContainer,
  CoverImage,
  Divider,
  Headline,
  ReadMore,
  StyledCoverEntityLink,
  StyledEntityLink,
  Topic,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: postMagazineRecommendedItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref: forwardReference,
  reference: reference_,
  topicLinkVariant,
}) => {
  const content = useFragment(
    graphql`
      fragment postMagazineRecommendedItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize800x1200: true
            withHeadline: true
            withTopicLink: true
          )
      }
    `,
    reference_,
  );
  const providers = useContentItemProviders(content, topicLinkVariant);
  const topic = providers.topicLink?.({ preferShortName: true });
  const headline = providers.headline({
    hideTooltip: true,
    preferSocialHeadline: true,
  });

  return (
    <Container className={className} ref={forwardReference}>
      <StyledCoverEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size800x1200",
              mobileUp: "size800x1200",
              tabletUp: "size800x1200",
            },
          })}
        </CoverImage>
      </StyledCoverEntityLink>
      <ContentContainer>
        <Topic>{topic}</Topic>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          reference={content}
        >
          <Headline>{headline}</Headline>
        </StyledEntityLink>
        <Divider />
        <ReadMore onClick={() => onClick?.(content.entityId, content.urlAlias)} reference={content}>
          READ MORE
        </ReadMore>
      </ContentContainer>
    </Container>
  );
};

Component.displayName = "ContentItemPostMagazineRecommended";

export const ContentItemPostMagazineRecommended = Component;
